import type { HeliosRecordDataTableColumnFormatting, HeliosRecordDataTablesColumn, HeliosRecordDataTablesData } from 'types/dataHelios';
import { TABLE_CELL_TYPE } from 'types/enums';
import { transformDataByDataSchema } from 'utils/common';

const DOT_SUBSTITUTE = '__DOT__';
export interface HeliosSchemeEntry {
  label: string;
  type: TABLE_CELL_TYPE;
  translationKey?: string;
  hidden?: boolean;
  customKey?: string;
  formatting?: HeliosRecordDataTableColumnFormatting;
}

export interface RequiredRowProps {
  id: string;
  name: string;
  value?: string;
}

export type RowProps = RequiredRowProps & Record<string, string>;

export const heliosBrowseEditDataScheme = {
  id: {
    label: 'id',
    type: TABLE_CELL_TYPE.ID,
    translationKey: 'table.header.id',
    hidden: true,
  },
  name: {
    label: 'name',
    type: TABLE_CELL_TYPE.TEXT,
    translationKey: 'table.header.name',
    hidden: true,
  },
};

export type HeliosSchemeType = typeof heliosBrowseEditDataScheme & Record<string, HeliosSchemeEntry>;

export const generateTableSchemaAndData = (
  columns?: HeliosRecordDataTablesColumn[] | undefined,
  data?: HeliosRecordDataTablesData[],
  translatedLabelsByItemId?: Record<number, string>
) => {
  const transformedColumns = (columns ?? []).map((column) => ({
    key: column.columnKey,
    label: column.label,
    itemId: column.itemId,
    formatting: column.formatting,
  }));

  const tableScheme: HeliosSchemeType = { ...heliosBrowseEditDataScheme };
  transformedColumns.forEach((column) => {
    const key = column.key.replace('.', DOT_SUBSTITUTE);
    const localizedLabel = translatedLabelsByItemId?.[column.itemId];

    tableScheme[key] = {
      label: key,
      customKey: localizedLabel ?? column.label,
      type: TABLE_CELL_TYPE.TEXT,
      formatting: column.formatting,
    };
  });

  const items = (data ?? []).map((record) => {
    const tableRow: RowProps = {
      id: record.recordNumber?.toString() ?? '',
      name: record.recordName,
    };
    record.items.forEach((item) => {
      const key = item.columnKey.replace('.', DOT_SUBSTITUTE);

      let formattedValue = item.formattedValue;
      if (formattedValue === null || formattedValue === undefined) {
        formattedValue = '';
      } else if (typeof formattedValue === 'object') {
        if (Object.keys(formattedValue).length === 0) {
          formattedValue = '';
        } else {
          formattedValue = JSON.stringify(formattedValue);
        }
      }
      tableRow[key] = formattedValue;
    });
    return tableRow;
  });

  const transformedData = transformDataByDataSchema(items, tableScheme);

  return { tableScheme, transformedData };
};
